import { useState } from 'react'
import { Dialog, DialogPanel, DialogTitle, DialogBackdrop, RadioGroup, Radio, Label, Field } from '@headlessui/react'
import Close from '../../assets/close.svg?react'
import { useReportReview } from '../../queries/useReviews'

const REPORT_REASONS = [
  'Inappropriate / offensive',
  'Spam',
  'Off-topic',
  'Misleading',
  'Low-effort',
] as const

interface IReportReviewDialogProps {
  isOpen: boolean
  onClose: () => void
  reviewId: string | null
}

export const ReportReviewDialog = (props: IReportReviewDialogProps) => {
  const { isOpen, onClose, reviewId } = props

  const [selectedReason, setSelectedReason] = useState<string>('')
  const [customReason, setCustomReason] = useState('')
  const [isOther, setIsOther] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const reportReviewMutation = useReportReview()

  const handleReasonChange = (value: string) => {
    if (value === 'other') {
      setIsOther(true)
      setSelectedReason('')
    } else {
      setIsOther(false)
      setSelectedReason(value)
      setCustomReason('')
    }
  }

  const handleConfirm = () => {
    if (!reviewId) return

    const reason = isOther ? customReason.trim() : selectedReason
    if (!reason) return

    setError(null)

    reportReviewMutation.mutate(
      { reviewId, reason },
      {
        onSuccess: () => {
          setIsSuccess(true)
        },
        onError: (error) => {
          console.error('Error reporting review:', error)
          setError('Failed to submit report. Please try again.')
        },
      }
    )
  }

  const handleClose = () => {
    // Reset state when closing
    setSelectedReason('')
    setCustomReason('')
    setIsOther(false)
    setError(null)
    setIsSuccess(false)
    onClose()
  }

  const isValid = isOther ? customReason.trim().length > 0 : selectedReason.length > 0

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl relative">
          <button
            onClick={handleClose}
            className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
            aria-label="Close"
          >
            <Close width={16} height={16} />
          </button>

          {isSuccess ? (
            <>
              <DialogTitle className="text-2xl font-semibold text-gray-900">
                Report Submitted
              </DialogTitle>
              <p className="mt-6 text-sm text-gray-600">
                Thank you for your report. We will review it shortly.
              </p>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleClose}
                  className="inline-flex items-center rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 cursor-pointer"
                >
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogTitle className="text-2xl font-semibold text-gray-900">
                Report Review
              </DialogTitle>

              <p className="mt-6 text-sm text-gray-600">
                Please select a reason for reporting this review:
              </p>

              <RadioGroup
                value={isOther ? 'other' : selectedReason}
                onChange={handleReasonChange}
                className="mt-4 space-y-2"
              >
                {REPORT_REASONS.map((reason) => (
                  <Field key={reason} className="flex items-center gap-3">
                    <Radio
                      value={reason}
                      className="group flex size-5 items-center justify-center rounded-full border border-gray-300 bg-white data-[checked]:border-black data-[checked]:bg-black cursor-pointer"
                    >
                      <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
                    </Radio>
                    <Label className="text-sm text-gray-700 cursor-pointer">{reason}</Label>
                  </Field>
                ))}

                {/* Other option */}
                <Field className="flex items-center gap-3">
                  <Radio
                    value="other"
                    className="group flex size-5 items-center justify-center rounded-full border border-gray-300 bg-white data-[checked]:border-black data-[checked]:bg-black cursor-pointer"
                  >
                    <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
                  </Radio>
                  <Label className="text-sm text-gray-700 cursor-pointer">Other</Label>
                </Field>
              </RadioGroup>

              {/* Custom reason text area */}
              {isOther && (
                <>
                  <textarea
                    value={customReason}
                    maxLength={400}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Enter reason..."
                    className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                    rows={3}
                  />
                  {error && (
                    <p className="mt-2 text-sm text-red-600">{error}</p>
                  )}
                </>
              )}

              {/* Error message for non-textarea errors */}
              {error && !isOther && (
                <p className="mt-3 text-sm text-red-600">{error}</p>
              )}

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={handleClose}
                  disabled={reportReviewMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!isValid || reportReviewMutation.isPending}
                  className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reportReviewMutation.isPending ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
